import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import { ROUTE_PATHS } from '../config/navigation';
import { hrSystems, onboardingJourney } from '../config/hrSystems';
import { ORGANIZATION_BRAND, corporateEmailPlaceholder } from '../config/organization';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  company: ORGANIZATION_BRAND.name,
  phone: '',
  department: '',
  position: '',
  startDate: '',
  birthDate: '',
  address: '',
  emergencyContact: '',
  officeLocation: '',
  extension: '',
  bio: '',
  expertise: '',
  password: '',
  confirmPassword: '',
  directoryVisible: true
};

const departmentOptions = [
  'İnsan Kaynakları',
  'Teknik Ofis',
  'Satın Alma',
  'Finans',
  'Muhasebe',
  'İdari İşler',
  'Bilgi Teknolojileri',
  'Proje Yönetimi'
];

const YeniCalisanPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const { toasts, addToast, removeToast } = useToast();

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      addToast('Lütfen zorunlu alanları doldurun.', 'error', 4000);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      addToast('Şifreler eşleşmiyor.', 'error', 4000);
      return;
    }

    if (formData.password.length < 6) {
      addToast('Şifre en az 6 karakter olmalı.', 'error', 4000);
      return;
    }

    try {
      setLoading(true);

      await api.createEmployee({
        ...formData,
        expertise: formData.expertise
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      });

      addToast('Yeni çalışan kaydı başarıyla oluşturuldu.', 'success', 4000);
      setFormData(initialFormData);
    } catch (error) {
      console.error('Create employee error:', error);
      addToast('Yeni çalışan kaydı oluşturulamadı.', 'error', 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <h1>Yeni Çalışan Kaydı</h1>
          <p>Profil açılışı, onboarding akışı ve erişim ihtiyaçlarını tek formda yönetin</p>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-xl-8">
            <div className="modern-hr-widget">
              <div className="modern-hr-widget-header">
                <div className="modern-hr-widget-title">
                  <i className="bx bx-user-plus"></i>
                  <h4>Çalışan bilgi formu</h4>
                </div>
                <p className="modern-hr-widget-subtitle">
                  Rehber görünürlüğü, temel profil alanları ve ilk gün hazırlık bilgileri aynı kayıtta tutulur.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="modern-hr-form">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="modern-hr-section">
                      <h5 className="modern-hr-section-title">
                        <i className="bx bx-id-card"></i>
                        Kisisel bilgiler
                      </h5>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Ad</label>
                        <input
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          required
                        />
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Soyad</label>
                        <input
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          required
                        />
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Kurum e-postasi</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          placeholder={corporateEmailPlaceholder}
                          required
                        />
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Şirket</label>
                        <input
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          placeholder={ORGANIZATION_BRAND.name}
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="modern-hr-form-group">
                            <label className="modern-hr-form-label">Telefon</label>
                            <input
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="modern-hr-form-input"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="modern-hr-form-group">
                            <label className="modern-hr-form-label">Dahili numara</label>
                            <input
                              name="extension"
                              value={formData.extension}
                              onChange={handleInputChange}
                              className="modern-hr-form-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="modern-hr-form-group">
                            <label className="modern-hr-form-label">Doğum tarihi</label>
                            <input
                              type="date"
                              name="birthDate"
                              value={formData.birthDate}
                              onChange={handleInputChange}
                              className="modern-hr-form-input"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="modern-hr-form-group">
                            <label className="modern-hr-form-label">Acil durum iletişimi</label>
                            <input
                              name="emergencyContact"
                              value={formData.emergencyContact}
                              onChange={handleInputChange}
                              className="modern-hr-form-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Adres</label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="modern-hr-form-textarea"
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="modern-hr-section">
                      <h5 className="modern-hr-section-title">
                        <i className="bx bx-briefcase-alt"></i>
                        İş ve rehber bilgileri
                      </h5>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Departman</label>
                        <select
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                          className="modern-hr-form-select"
                          required
                        >
                          <option value="">Departman seçin</option>
                          {departmentOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Pozisyon</label>
                        <input
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          required
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="modern-hr-form-group">
                            <label className="modern-hr-form-label">İşe başlama tarihi</label>
                            <input
                              type="date"
                              name="startDate"
                              value={formData.startDate}
                              onChange={handleInputChange}
                              className="modern-hr-form-input"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="modern-hr-form-group">
                            <label className="modern-hr-form-label">Ofis / lokasyon</label>
                            <input
                              name="officeLocation"
                              value={formData.officeLocation}
                              onChange={handleInputChange}
                              className="modern-hr-form-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Kısa biyografi</label>
                        <textarea
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          className="modern-hr-form-textarea"
                          rows="3"
                          placeholder="Rehberde gösterilecek kısa tanıtım metni"
                        />
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Uzmanlık alanları</label>
                        <input
                          name="expertise"
                          value={formData.expertise}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          placeholder="Planlama, saha yönetimi, raporlama"
                        />
                      </div>

                      <div className="form-check mb-3">
                        <input
                          id="directoryVisible"
                          name="directoryVisible"
                          type="checkbox"
                          checked={formData.directoryVisible}
                          onChange={handleInputChange}
                          className="form-check-input"
                        />
                        <label htmlFor="directoryVisible" className="form-check-label">
                          Çalışan rehberinde görünür olsun
                        </label>
                      </div>
                    </div>

                    <div className="modern-hr-section">
                      <h5 className="modern-hr-section-title">
                        <i className="bx bx-lock-alt"></i>
                        Giriş bilgileri
                      </h5>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Şifre</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          required
                        />
                      </div>

                      <div className="modern-hr-form-group">
                        <label className="modern-hr-form-label">Şifre tekrarı</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="modern-hr-form-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modern-hr-form-actions">
                  <button
                    type="button"
                    className="modern-hr-btn modern-hr-btn-secondary"
                    onClick={() => window.history.back()}
                  >
                    Geri dön
                  </button>
                  <button type="submit" className="modern-hr-btn modern-hr-btn-primary" disabled={loading}>
                    {loading ? 'Kaydediliyor...' : 'Çalışanı kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="col-xl-4">
            <div className="modern-hr-widget mb-4">
              <div className="modern-hr-widget-header">
                <div className="modern-hr-widget-title">
                  <i className="bx bx-git-branch"></i>
                  <h4>Onboarding akışı</h4>
                </div>
                <p className="modern-hr-widget-subtitle">
                  Kayıt açıldıktan sonra ilerleyen 30 günlük sorumluluk omurgası
                </p>
              </div>

              <div className="d-flex flex-column gap-3">
                {onboardingJourney.map((step) => (
                  <div key={step.id} className="hr-job-card">
                    <div className="hr-job-card-top">
                      <span className="hr-job-badge">{step.timeline}</span>
                      <small>{step.owner}</small>
                    </div>
                    <h5>{step.title}</h5>
                    <p>{step.tasks.join(' • ')}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="modern-hr-widget">
              <div className="modern-hr-widget-header">
                <div className="modern-hr-widget-title">
                  <i className="bx bx-link-alt"></i>
                  <h4>Hazırlık kanalları</h4>
                </div>
              </div>

              <div className="hr-system-links">
                {hrSystems.map((system) =>
                  system.isExternal ? (
                    <a
                      key={system.key}
                      href={system.href}
                      className="hr-system-link-card"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className={system.icon}></i>
                      <div>
                        <strong>{system.title}</strong>
                        <span>{system.subtitle}</span>
                        <small>{system.ctaLabel}</small>
                      </div>
                    </a>
                  ) : (
                    <Link key={system.key} to={system.href} className="hr-system-link-card">
                      <i className={system.icon}></i>
                      <div>
                        <strong>{system.title}</strong>
                        <span>{system.subtitle}</span>
                        <small>{system.helperText}</small>
                      </div>
                    </Link>
                  )
                )}
              </div>

              <div className="mt-4">
                <Link className="modern-hr-btn modern-hr-btn-secondary w-100" to={ROUTE_PATHS.hr}>
                  İK merkezine geri dön
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default YeniCalisanPage;
